import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, viewChild } from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { heroCheckCircle, heroExclamationTriangle, heroInformationCircle, heroXCircle } from '@ng-icons/heroicons/outline';
import { NGXLogger } from 'ngx-logger';
import { ConfirmPopup } from 'primeng/confirmpopup';
import { ClickStopPropagation } from '../../../core/directives/click-stop-propagation';
import { ConfirmPopupService } from '../../../core/services/confirm-popup.service';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-confirm-popup',
  imports: [
    CommonModule,
    ConfirmPopup,
    NgIconComponent,
    ButtonComponent,
    ClickStopPropagation,
  ],
  templateUrl: './confirm-popup.component.html',
  styleUrl: './confirm-popup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideIcons({
      heroExclamationTriangle,
      heroInformationCircle,
      heroCheckCircle,
      heroXCircle,
    }),
  ],
})
export class ConfirmPopupComponent {
  private confirmPopupService = inject(ConfirmPopupService);
  private logger = inject(NGXLogger);

  confirmPopup = viewChild.required<ConfirmPopup>('confirmPopup');
  isSaving = this.confirmPopupService.isSaving;

  readonly confirmationState = this.confirmPopupService.currentConfirmation;

  readonly confirmationClass = computed(() => {
    const state = this.confirmationState();
    return this.confirmPopupService.getStyleClass(state.type);
  });

  readonly confirmationIcon = computed(() => {
    const state = this.confirmationState();
    return this.confirmPopupService.getIconName(state.type);
  });

  readonly confirmationAcceptLabel = computed(() => {
    const state = this.confirmationState();
    return state.acceptLabel;
  });

  readonly confirmationRejectLabel = computed(() => {
    const state = this.confirmationState();
    return state.rejectLabel;
  });

  readonly confirmationMessage = computed(() => {
    const state = this.confirmationState();
    return state.message;
  });

  readonly confirmationHandleSaving = computed(() => {
    const state = this.confirmationState();
    return state.handleSaving;
  });

  readonly confirmationShowAcceptButton = computed(() => {
    const state = this.confirmationState();
    this.logger.debug('confirmationShowAcceptButton', state.showAcceptButton);

    return state.showAcceptButton;
  });

  readonly confirmationShowRejectButton = computed(() => {
    const state = this.confirmationState();
    return state.showRejectButton;
  });

  onAcceptClick(): void {
    // Call our custom accept handler that doesn't close the popup
    this.confirmPopupService.acceptClick();
  }
}
